import { WhiteboardState } from '@components/Whiteboard/types';
import { ThemeName } from '@src/theme/theme';
import { TrackType } from '@withso/pop-media-sdk';

// eslint-disable-next-line no-shadow
export enum WidgetType {
  Link = 'LINK',
  StickyNote = 'STICKY_NOTE',
  Whiteboard = 'WHITEBOARD',
  YouTube = 'YOU_TUBE',
  Notepad = 'NOTEPAD',
  SidecarStream = 'SIDECAR_STREAM',
  Huddle = 'HUDDLE',
  File = 'FILE',
  Chat = 'CHAT',
  RoomPortal = 'ROOM_PORTAL',
  // not used by end-users - this is just for demos
  MockUser = 'MOCK_USER',
  Board = 'BOARD',
  Game = 'GAME',
  PdfPresentation = 'PDF_PRESENTATION',
}

/**
 * A common data interface related to media playback which
 * is synchronized between peers
 */
export interface WidgetMediaState {
  timestamp?: number;
  /**
   * If the media is currently playing,
   * this records when it started. The client can use
   * this to compute a more exact timestamp, by adding
   * the elapsed time since play started to the recorded
   * timestamp.
   */
  playStartedTimestampUtc: string | null;
  isPlaying?: boolean;
  isRepeatOn?: boolean;
  /** 0-1 */
  volume?: number;
}

export interface StickyNoteWidgetState {
  title?: string;
  text: string;
  color?: ThemeName;
}

export interface LinkWidgetState {
  url: string;
  title?: string;
  iconUrl?: string | null;
  /**
   * Should everyone see this link as an iframe?
   */
  showIframe?: boolean;
  /**
   * If the link has any embeddable content, this property will
   * be the URL to provide to the iframe.
   */
  iframeUrl?: string | null;
}

export interface FileWidgetState {
  url: string;
  fileName: string;
  fileId: string;
  contentType: string;
  uploadProgress: number;
  mediaState?: WidgetMediaState;
}

export interface ChatWidgetState {
  title?: string;
  color?: ThemeName;
}
export interface ChatMessageShape {
  id: string;
  content: string;
  sender: {
    id: string;
    displayName: string;
  };
  createdAt: string;
}

export interface RoomPortalWidgetState {
  targetRoomRoute?: string;
  label?: string;
  tooltip?: string;
  visualStyle?: 'pin' | 'card';
  adminColor?: string;
  adminOpacity?: number;
  isClosed?: boolean;
  closedMessage?: string;
  accessCode?: string;
}

export interface NotepadState {
  docId: string;
  initialData?: null | any;
  title?: string;
  color?: ThemeName;
}

export interface WhiteboardWidgetState {
  whiteboardState: WhiteboardState;
  accessMode?: 'editors' | 'viewers';
  followViewport?: boolean;
  viewportLeaderId?: string;
}

export interface YoutubeWidgetState {
  mediaState?: WidgetMediaState;
  videoId: string;
}

export interface BoardWidgetState {
  docId: string;
  title?: string;
  color?: ThemeName;
}

export interface GameWidgetState {
  title?: string;
  gameId?: string;
  gameType?: string;
  role?: string;
  url?: string;
  color?: ThemeName;
  sessionLinks?: Array<{
    title: string;
    role: string;
    url: string;
  }>;
}

// nothing yet.
export interface SidecarStreamWidgetState {
  mediaParticipantId: string;
  videoTrackType?: TrackType;
  audioTrackType?: TrackType;
}

export interface MockUserWidgetState {
  displayName?: string;
  avatarSeed?: string;
  mockGroupId?: string;
  mockGroupName?: string;
  mockGroupColor?: string;
}

export type AnnotationToolKind = 'select' | 'pen' | 'eraser' | 'arrow' | 'rect' | 'ellipse' | 'text';

export interface AnnotationShape {
  id: string;
  kind: 'line' | 'arrow' | 'rect' | 'ellipse' | 'text';
  color: string;
  strokeWidth: number;
  /** kind === 'line' only */
  isEraser?: boolean;
  /** kind === 'line' | 'arrow' (flat [x1, y1, x2, y2, ...] pairs, relative to x/y) */
  points?: number[];
  /** Translation offset applied on top of points/position - written by drag/resize. */
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  /** kind === 'text' */
  text?: string;
  fontSize?: number;
}

export interface PdfPresentationPageState {
  shapes: AnnotationShape[];
}

export interface PdfPresentationWidgetState {
  pdfUrl: string;
  fileId: string;
  fileName: string;
  uploadProgress: number;
  totalPages?: number;
  currentPageIndex: number;
  pages: Record<number, PdfPresentationPageState>;
  followPresenter?: boolean;
  presenterId?: string;
}

export interface HuddleWidgetState {
  title?: string;
  color?: ThemeName;
  groupId?: string;
  autoGroupHuddle?: boolean;
  huddleType?: 'neutral' | 'team';
  accessPolicy?: 'open' | 'team_only' | 'master_only';
  fillStyle?: 'soft' | 'frosted' | 'solid' | 'privacy';
  opacity?: number;
  blurPx?: number;
  shape?: 'rounded' | 'square';
}

/** Common properties to all widgets */
export interface BaseWidgetShape {
  widgetId: string;
  creatorId: string;
  creatorDisplayName: string;
}

// FIXME: can this be automatically created using TypeScript generics?
export type WidgetStateByType = {
  [WidgetType.StickyNote]: StickyNoteWidgetState;
  [WidgetType.Link]: LinkWidgetState;
  [WidgetType.Whiteboard]: WhiteboardWidgetState;
  [WidgetType.Notepad]: NotepadState;
  [WidgetType.YouTube]: YoutubeWidgetState;
  [WidgetType.SidecarStream]: SidecarStreamWidgetState;
  [WidgetType.MockUser]: MockUserWidgetState;
  [WidgetType.Huddle]: HuddleWidgetState;
  [WidgetType.File]: FileWidgetState;
  [WidgetType.Chat]: ChatWidgetState;
  [WidgetType.RoomPortal]: RoomPortalWidgetState;
  [WidgetType.Board]: BoardWidgetState;
  [WidgetType.Game]: GameWidgetState;
  [WidgetType.PdfPresentation]: PdfPresentationWidgetState;
};
export type WidgetShapeByType = {
  [WidgetType.StickyNote]: BaseWidgetShape & {
    widgetState: StickyNoteWidgetState;
  };
  [WidgetType.Link]: BaseWidgetShape & {
    widgetState: LinkWidgetState;
  };
  [WidgetType.Whiteboard]: BaseWidgetShape & {
    widgetState: WhiteboardWidgetState;
  };
  [WidgetType.Notepad]: BaseWidgetShape & {
    widgetState: NotepadState;
  };
  [WidgetType.YouTube]: BaseWidgetShape & {
    widgetState: YoutubeWidgetState;
  };
  [WidgetType.SidecarStream]: BaseWidgetShape & {
    widgetState: SidecarStreamWidgetState;
  };
  [WidgetType.MockUser]: BaseWidgetShape & {
    widgetState: MockUserWidgetState;
  };
  [WidgetType.Huddle]: BaseWidgetShape & {
    widgetState: HuddleWidgetState;
  };
  [WidgetType.File]: BaseWidgetShape & {
    widgetState: FileWidgetState;
  };
  [WidgetType.Chat]: BaseWidgetShape & {
    widgetState: ChatWidgetState;
    messages: {
      hasMoreToLoad: boolean;
      messageList: ChatMessageShape[];
    };
  };
  [WidgetType.RoomPortal]: BaseWidgetShape & {
    widgetState: RoomPortalWidgetState;
  };
  [WidgetType.Board]: BaseWidgetShape & {
    widgetState: BoardWidgetState;
  };
  [WidgetType.Game]: BaseWidgetShape & {
    widgetState: GameWidgetState;
  };
  [WidgetType.PdfPresentation]: BaseWidgetShape & {
    widgetState: PdfPresentationWidgetState;
  };
};
export type WidgetShapeForType<T extends WidgetType> = WidgetShapeTable[T];

type TagWithKey<TagName extends string, T> = {
  [K in keyof T]: { [_ in TagName]: K } & T[K];
};

type Unionize<T> = T[keyof T];

type WidgetShapeTable = TagWithKey<'type', WidgetShapeByType>;

export type LinkWidgetShape = WidgetShapeTable[WidgetType.Link];
export type StickyNoteWidgetShape = WidgetShapeTable[WidgetType.StickyNote];
export type WhiteboardWidgetShape = WidgetShapeTable[WidgetType.Whiteboard];
export type YoutubeWidgetShape = WidgetShapeTable[WidgetType.YouTube];
export type ScreenShareWidgetShape = WidgetShapeTable[WidgetType.SidecarStream];
export type MockUserWidgetShape = WidgetShapeTable[WidgetType.MockUser];
export type HuddleWidgetShape = WidgetShapeTable[WidgetType.Huddle];
export type FileWidgetShape = WidgetShapeTable[WidgetType.File];
export type ChatWidgetShape = WidgetShapeTable[WidgetType.Chat];
export type RoomPortalWidgetShape = WidgetShapeTable[WidgetType.RoomPortal];
export type GameWidgetShape = WidgetShapeTable[WidgetType.Game];
export type PdfPresentationWidgetShape = WidgetShapeTable[WidgetType.PdfPresentation];

export type WidgetShape = Unionize<WidgetShapeTable>;

export type WidgetState = WidgetShape['widgetState'];
